import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiCreditCard, FiSearch, FiDownload, FiEye } from 'react-icons/fi';
import './PembayaranMahasiswa.css';

const PembayaranMahasiswa = () => {
  const [transaksiList, setTransaksiList] = useState([]);
  const [mahasiswaList, setMahasiswaList] = useState([]);
  const [biayaList, setBiayaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    mahasiswa_id: '',
    jenis_biaya: '',
    nominal_biaya: '',
    nominal_dibayar: '',
    tanggal_bayar: new Date().toISOString().split('T')[0],
    metode_pembayaran: 'Transfer',
    keterangan: '',
    bukti_pembayaran: '',
    status_pembayaran: 'Lunas'
  });
  const [selectedTransaksi, setSelectedTransaksi] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterProdi, setFilterProdi] = useState('');
  const [searchNim, setSearchNim] = useState('');
  const [searchNama, setSearchNama] = useState('');
  
  // State untuk searchable mahasiswa dropdown
  const [mahasiswaSearch, setMahasiswaSearch] = useState('');
  const [showMahasiswaDropdown, setShowMahasiswaDropdown] = useState(false);
  const [selectedMahasiswa, setSelectedMahasiswa] = useState(null);
  
  // State untuk tagihan mahasiswa
  const [tagihanList, setTagihanList] = useState([]);
  const [loadingTagihan, setLoadingTagihan] = useState(false);
  const [selectedTagihan, setSelectedTagihan] = useState(null);

  const programStudiOptions = [
    'Profesi Ners',
    'S1 Keperawatan',
    'S1 Kesehatan Masyarakat'
  ];

  const metodePembayaranOptions = [
    'Transfer',
    'Tunai',
    'VA (Virtual Account)',
    'Kartu Kredit/Debit',
    'E-Wallet'
  ];

  const statusPembayaranOptions = [
    'Lunas',
    'Belum Lunas',
    'Cicilan'
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      const [transaksiRes, mahasiswaRes, biayaRes] = await Promise.all([
        axios.get('/api/finance/pembayaran-mahasiswa'),
        axios.get('/api/students', { params: { limit: 10000 } }), // Request semua mahasiswa
        axios.get('/api/finance/setting-pembiayaan')
      ]);
      
      setTransaksiList(transaksiRes.data.data);
      setMahasiswaList(mahasiswaRes.data.data);
      setBiayaList(biayaRes.data.data);
    } catch (error) {
      toast.error('Gagal mengambil data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle click outside untuk close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMahasiswaDropdown && !event.target.closest('.searchable-select')) {
        setShowMahasiswaDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMahasiswaDropdown]);

  const handleOpenModal = (transaksi = null) => {
    if (transaksi) {
      setEditMode(true);
      setSelectedTransaksi(transaksi);
      setFormData({
        mahasiswa_id: transaksi.mahasiswa_id._id,
        jenis_biaya: transaksi.jenis_biaya,
        nominal_biaya: transaksi.nominal_biaya,
        nominal_dibayar: transaksi.nominal_dibayar,
        tanggal_bayar: transaksi.tanggal_bayar.split('T')[0],
        metode_pembayaran: transaksi.metode_pembayaran,
        keterangan: transaksi.keterangan || '',
        bukti_pembayaran: transaksi.bukti_pembayaran || '',
        status_pembayaran: transaksi.status_pembayaran
      });
      // Set selected mahasiswa untuk edit mode
      setSelectedMahasiswa(transaksi.mahasiswa_id);
      setMahasiswaSearch(`${transaksi.mahasiswa_id.nim} - ${transaksi.mahasiswa_id.name}`);
      // Tidak perlu fetch tagihan di edit mode
    } else {
      setEditMode(false);
      setSelectedTransaksi(null);
      setFormData({
        mahasiswa_id: '',
        jenis_biaya: '',
        nominal_biaya: '',
        nominal_dibayar: '',
        tanggal_bayar: new Date().toISOString().split('T')[0],
        metode_pembayaran: 'Transfer',
        keterangan: '',
        bukti_pembayaran: '',
        status_pembayaran: 'Lunas'
      });
      // Reset mahasiswa search
      setSelectedMahasiswa(null);
      setMahasiswaSearch('');
      setTagihanList([]);
      setSelectedTagihan(null);
    }
    setShowModal(true);
    setShowMahasiswaDropdown(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedTransaksi(null);
    setMahasiswaSearch('');
    setSelectedMahasiswa(null);
    setShowMahasiswaDropdown(false);
    setTagihanList([]);
    setSelectedTagihan(null);
  };

  const handleViewDetail = (transaksi) => {
    setSelectedTransaksi(transaksi);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedTransaksi(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle search input untuk mahasiswa
  const handleMahasiswaSearchChange = (e) => {
    const value = e.target.value;
    setMahasiswaSearch(value);
    setShowMahasiswaDropdown(true);
    
    // Clear selection jika user mengetik lagi
    if (selectedMahasiswa) {
      setSelectedMahasiswa(null);
      setFormData(prev => ({ ...prev, mahasiswa_id: '' }));
    }
  };

  // Filter mahasiswa berdasarkan search - tampilkan semua mahasiswa
  const filteredMahasiswaOptions = mahasiswaList
    .filter(mhs => {
      // Jika search kosong, tampilkan semua mahasiswa
      if (!mahasiswaSearch || mahasiswaSearch.trim() === '') {
        return true;
      }
      
      const searchLower = mahasiswaSearch.toLowerCase();
      return (
        mhs.nim?.toLowerCase().includes(searchLower) ||
        mhs.name?.toLowerCase().includes(searchLower) ||
        mhs.program?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => {
      // Urutkan berdasarkan NIM (ascending)
      if (a.nim && b.nim) {
        return a.nim.localeCompare(b.nim);
      }
      return 0;
    });

  // Handle select mahasiswa dari dropdown
  const handleSelectMahasiswa = async (mahasiswa) => {
    setSelectedMahasiswa(mahasiswa);
    setMahasiswaSearch(`${mahasiswa.nim} - ${mahasiswa.name}`);
    setFormData(prev => ({ ...prev, mahasiswa_id: mahasiswa._id }));
    setShowMahasiswaDropdown(false);
    
    // Fetch tagihan mahasiswa yang belum lunas
    await fetchTagihanMahasiswa(mahasiswa.nim);
    
    // Auto-fill biaya yang tersedia untuk mahasiswa
    if (mahasiswa) {
      // Filter biaya berdasarkan program studi mahasiswa
      const availableBiaya = biayaList.filter(b => 
        b.program_studi === mahasiswa.program || 
        b.program_studi === 'Semua Program Studi'
      );
      console.log('Available biaya:', availableBiaya);
      // Anda bisa menambahkan logic untuk suggest biaya
    }
  };

  // Fetch tagihan mahasiswa
  const fetchTagihanMahasiswa = async (nim) => {
    try {
      setLoadingTagihan(true);
      const response = await axios.get(`/api/tagihan`, {
        params: { nim, status: 'belum_lunas' }
      });
      setTagihanList(response.data.data || []);
    } catch (error) {
      console.error('Error fetching tagihan:', error);
      setTagihanList([]);
    } finally {
      setLoadingTagihan(false);
    }
  };

  // Handle pilih tagihan
  const handleSelectTagihan = (tagihan) => {
    setSelectedTagihan(tagihan);
    setFormData(prev => ({
      ...prev,
      jenis_biaya: tagihan.jenis_tagihan,
      nominal_biaya: tagihan.jumlah,
      nominal_dibayar: tagihan.jumlah,
      keterangan: `Pembayaran ${tagihan.jenis_tagihan} - ${tagihan.semester} ${tagihan.tahun_ajaran}`
    }));
  };

  const handleJenisBiayaChange = (e) => {
    const jenisBiaya = e.target.value;
    setFormData(prev => ({ ...prev, jenis_biaya: jenisBiaya }));
    
    // Auto-fill nominal dari setting pembiayaan
    const biaya = biayaList.find(b => b.jenis_biaya === jenisBiaya);
    if (biaya) {
      setFormData(prev => ({ 
        ...prev, 
        nominal_biaya: biaya.nominal,
        nominal_dibayar: biaya.nominal
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.mahasiswa_id || !formData.jenis_biaya || !formData.nominal_dibayar) {
      toast.error('Mohon lengkapi data yang wajib diisi');
      return;
    }

    try {
      // Tambahkan tagihan_id jika ada tagihan yang dipilih
      const submitData = {
        ...formData,
        tagihan_id: selectedTagihan?._id || null
      };

      if (editMode) {
        await axios.put(`/api/finance/pembayaran-mahasiswa/${selectedTransaksi._id}`, submitData);
        toast.success('Transaksi berhasil diupdate');
      } else {
        await axios.post('/api/finance/pembayaran-mahasiswa', submitData);
        toast.success('Transaksi berhasil ditambahkan');
        
        // Jika ada tagihan yang dipilih, beri notifikasi
        if (selectedTagihan) {
          toast.info(`Status tagihan "${selectedTagihan.jenis_tagihan}" telah diupdate menjadi lunas`);
        }
      }
      
      handleCloseModal();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Terjadi kesalahan');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus transaksi ini?')) {
      try {
        await axios.delete(`/api/finance/pembayaran-mahasiswa/${id}`);
        toast.success('Transaksi berhasil dihapus');
        fetchData();
      } catch (error) {
        toast.error('Gagal menghapus transaksi');
      }
    }
  };

  const formatRupiah = (angka) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(angka);
  };

  const formatTanggal = (tanggal) => {
    return new Date(tanggal).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const filteredTransaksi = transaksiList.filter(transaksi => {
    const matchStatus = !filterStatus || transaksi.status_pembayaran === filterStatus;
    const matchProdi = !filterProdi || transaksi.mahasiswa_id?.program === filterProdi;
    const matchNim = !searchNim || transaksi.mahasiswa_id?.nim.toLowerCase().includes(searchNim.toLowerCase());
    const matchNama = !searchNama || transaksi.mahasiswa_id?.name.toLowerCase().includes(searchNama.toLowerCase());
    return matchStatus && matchProdi && matchNim && matchNama;
  });

  const totalPembayaran = filteredTransaksi.reduce((sum, t) => sum + t.nominal_dibayar, 0);
  const totalLunas = filteredTransaksi.filter(t => t.status_pembayaran === 'Lunas').length;
  const totalBelumLunas = filteredTransaksi.filter(t => t.status_pembayaran === 'Belum Lunas').length;

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="pembayaran-mahasiswa-page">
      {/* Header */}
      <div className="modern-header">
        <div className="header-background">
          <div className="header-pattern"></div>
        </div>
        <div className="header-content">
          <div className="header-text">
            <div className="header-icon-wrapper">
              <FiCreditCard className="header-icon" />
            </div>
            <div>
              <h1>Pembayaran Mahasiswa</h1>
              <p>Kelola transaksi pembayaran biaya mahasiswa</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn btn-glass" onClick={() => handleOpenModal()}>
              <FiPlus /> Tambah Transaksi
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <FiCreditCard />
          </div>
          <div className="stat-details">
            <h3>{formatRupiah(totalPembayaran)}</h3>
            <p>Total Pembayaran</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
            <FiCreditCard />
          </div>
          <div className="stat-details">
            <h3>{totalLunas}</h3>
            <p>Transaksi Lunas</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
            <FiCreditCard />
          </div>
          <div className="stat-details">
            <h3>{totalBelumLunas}</h3>
            <p>Belum Lunas</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="filters">
          <div className="search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Cari NIM..."
              value={searchNim}
              onChange={(e) => setSearchNim(e.target.value)}
            />
          </div>
          <div className="search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Cari Nama..."
              value={searchNama}
              onChange={(e) => setSearchNama(e.target.value)}
            />
          </div>
          <div className="search-box">
            <select 
              className="filter-select"
              value={filterProdi}
              onChange={(e) => setFilterProdi(e.target.value)}
            >
              <option value="">Semua Program Studi</option>
              {programStudiOptions.map(prodi => (
                <option key={prodi} value={prodi}>{prodi}</option>
              ))}
            </select>
          </div>
          <div className="search-box">
            <select 
              className="filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">Semua Status</option>
              {statusPembayaranOptions.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="modern-table-container">
          <table className="modern-table">
            <thead>
              <tr>
                <th>No</th>
                <th>Tanggal</th>
                <th>NIM</th>
                <th>Nama Mahasiswa</th>
                <th>Program Studi</th>
                <th>Jenis Biaya</th>
                <th>Nominal Biaya</th>
                <th>Dibayar</th>
                <th>Metode</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransaksi.length === 0 ? (
                <tr>
                  <td colSpan="11" style={{ textAlign: 'center', padding: '40px' }}>
                    Tidak ada data transaksi
                  </td>
                </tr>
              ) : (
                filteredTransaksi.map((transaksi, index) => (
                  <tr key={transaksi._id}>
                    <td>{index + 1}</td>
                    <td>{formatTanggal(transaksi.tanggal_bayar)}</td>
                    <td><strong>{transaksi.mahasiswa_id?.nim || '-'}</strong></td>
                    <td>{transaksi.mahasiswa_id?.name || '-'}</td>
                    <td>{transaksi.mahasiswa_id?.program || '-'}</td>
                    <td>{transaksi.jenis_biaya}</td>
                    <td>{formatRupiah(transaksi.nominal_biaya)}</td>
                    <td><strong style={{ color: '#10b981' }}>{formatRupiah(transaksi.nominal_dibayar)}</strong></td>
                    <td>{transaksi.metode_pembayaran}</td>
                    <td>
                      <span className={`badge ${
                        transaksi.status_pembayaran === 'Lunas' ? 'badge-success' :
                        transaksi.status_pembayaran === 'Belum Lunas' ? 'badge-danger' :
                        'badge-warning'
                      }`}>
                        {transaksi.status_pembayaran}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="table-btn table-btn-view"
                          onClick={() => handleViewDetail(transaksi)}
                          title="Lihat Detail"
                        >
                          <FiEye />
                        </button>
                        <button
                          className="table-btn table-btn-edit"
                          onClick={() => handleOpenModal(transaksi)}
                          title="Edit"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          className="table-btn table-btn-delete"
                          onClick={() => handleDelete(transaksi._id)}
                          title="Hapus"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Form */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Transaksi' : 'Tambah Transaksi Baru'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Mahasiswa <span className="required">*</span></label>
                    <div className="searchable-select">
                      <div className="search-input-wrapper">
                        <FiSearch className="search-icon" />
                        <input
                          type="text"
                          value={mahasiswaSearch}
                          onChange={handleMahasiswaSearchChange}
                          onFocus={() => setShowMahasiswaDropdown(true)}
                          placeholder="Cari berdasarkan NIM, Nama, atau Program Studi..."
                          className="form-control search-input"
                          autoComplete="off"
                          required={!selectedMahasiswa}
                        />
                      </div>
                      {showMahasiswaDropdown && (
                        <div className="searchable-dropdown">
                          {filteredMahasiswaOptions.length > 0 ? (
                            <>
                              <div className="dropdown-header">
                                {mahasiswaSearch 
                                  ? `Ditemukan ${filteredMahasiswaOptions.length} mahasiswa`
                                  : `Total ${filteredMahasiswaOptions.length} mahasiswa di database`
                                }
                              </div>
                              {filteredMahasiswaOptions.map(mhs => (
                                <div
                                  key={mhs._id}
                                  className="dropdown-item"
                                  onClick={() => handleSelectMahasiswa(mhs)}
                                >
                                  <div className="dropdown-item-main">
                                    <strong>{mhs.nim}</strong> - {mhs.name}
                                  </div>
                                  <div className="dropdown-item-sub">
                                    <span className="program-badge">
                                      {mhs.program}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </>
                          ) : (
                            <div className="dropdown-item-empty">
                              Mahasiswa tidak ditemukan
                            </div>
                          )}
                        </div>
                      )}
                      {selectedMahasiswa && (
                        <div className="selected-info">
                          <small>
                            <strong>Program:</strong> {selectedMahasiswa.program}
                          </small>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Section Tagihan Mahasiswa */}
                  {!editMode && selectedMahasiswa && tagihanList.length > 0 && (
                    <div className="form-group full-width">
                      <label>Tagihan Belum Lunas</label>
                      <div className="tagihan-list">
                        {loadingTagihan ? (
                          <div className="tagihan-loading">Memuat tagihan...</div>
                        ) : (
                          tagihanList.map(tagihan => (
                            <div
                              key={tagihan._id}
                              className={`tagihan-item ${selectedTagihan?._id === tagihan._id ? 'selected' : ''}`}
                              onClick={() => handleSelectTagihan(tagihan)}
                            >
                              <div className="tagihan-info">
                                <div className="tagihan-title">
                                  <strong>{tagihan.jenis_tagihan}</strong>
                                  <span className={`tagihan-badge ${
                                    new Date(tagihan.deadline) < new Date() ? 'badge-danger' : 'badge-warning'
                                  }`}>
                                    {new Date(tagihan.deadline) < new Date() ? 'Terlambat' : 'Belum Lunas'}
                                  </span>
                                </div>
                                <div className="tagihan-details">
                                  <span>{tagihan.semester} - {tagihan.tahun_ajaran}</span>
                                  <span className="tagihan-deadline">
                                    Deadline: {formatTanggal(tagihan.deadline)}
                                  </span>
                                </div>
                              </div>
                              <div className="tagihan-amount">
                                {formatRupiah(tagihan.jumlah)}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      <small className="form-hint">
                        💡 Klik pada tagihan untuk mengisi form pembayaran secara otomatis
                      </small>
                    </div>
                  )}

                  {!editMode && selectedMahasiswa && tagihanList.length === 0 && !loadingTagihan && (
                    <div className="form-group full-width">
                      <div className="no-tagihan-info">
                        ✅ Mahasiswa tidak memiliki tagihan yang belum lunas
                      </div>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Tanggal Bayar <span className="required">*</span></label>
                    <input
                      type="date"
                      name="tanggal_bayar"
                      value={formData.tanggal_bayar}
                      onChange={handleChange}
                      className="form-control"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Jenis Biaya <span className="required">*</span></label>
                    <select
                      name="jenis_biaya"
                      value={formData.jenis_biaya}
                      onChange={handleJenisBiayaChange}
                      className="form-control"
                      required
                    >
                      <option value="">Pilih Jenis Biaya</option>
                      {biayaList.map(biaya => (
                        <option key={biaya._id} value={biaya.jenis_biaya}>
                          {biaya.jenis_biaya} - {formatRupiah(biaya.nominal)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Nominal Biaya <span className="required">*</span></label>
                    <input
                      type="number"
                      name="nominal_biaya"
                      value={formData.nominal_biaya}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Nominal biaya"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Nominal Dibayar <span className="required">*</span></label>
                    <input
                      type="number"
                      name="nominal_dibayar"
                      value={formData.nominal_dibayar}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Jumlah yang dibayar"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Metode Pembayaran</label>
                    <select
                      name="metode_pembayaran"
                      value={formData.metode_pembayaran}
                      onChange={handleChange}
                      className="form-control"
                    >
                      {metodePembayaranOptions.map(metode => (
                        <option key={metode} value={metode}>{metode}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Status Pembayaran</label>
                    <select
                      name="status_pembayaran"
                      value={formData.status_pembayaran}
                      onChange={handleChange}
                      className="form-control"
                    >
                      {statusPembayaranOptions.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Bukti Pembayaran</label>
                    <input
                      type="text"
                      name="bukti_pembayaran"
                      value={formData.bukti_pembayaran}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="No. Rekening/ID Transaksi"
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Keterangan</label>
                    <textarea
                      name="keterangan"
                      value={formData.keterangan}
                      onChange={handleChange}
                      className="form-control"
                      rows="2"
                      placeholder="Keterangan tambahan (opsional)"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  <FiX /> Batal
                </button>
                <button type="submit" className="btn btn-primary">
                  <FiSave /> {editMode ? 'Update' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Detail */}
      {showDetailModal && selectedTransaksi && (
        <div className="modal-overlay" onClick={handleCloseDetailModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Detail Transaksi Pembayaran</h3>
              <button className="modal-close" onClick={handleCloseDetailModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h4>Informasi Mahasiswa</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>NIM</label>
                    <span>{selectedTransaksi.mahasiswa_id?.nim || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Nama</label>
                    <span>{selectedTransaksi.mahasiswa_id?.name || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Program Studi</label>
                    <span>{selectedTransaksi.mahasiswa_id?.program || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Angkatan</label>
                    <span>{selectedTransaksi.mahasiswa_id?.angkatan || '-'}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Informasi Pembayaran</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Jenis Biaya</label>
                    <span><strong>{selectedTransaksi.jenis_biaya}</strong></span>
                  </div>
                  <div className="detail-item">
                    <label>Tanggal Bayar</label>
                    <span>{formatTanggal(selectedTransaksi.tanggal_bayar)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Nominal Biaya</label>
                    <span>{formatRupiah(selectedTransaksi.nominal_biaya)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Nominal Dibayar</label>
                    <span><strong style={{ color: '#10b981' }}>{formatRupiah(selectedTransaksi.nominal_dibayar)}</strong></span>
                  </div>
                  <div className="detail-item">
                    <label>Metode Pembayaran</label>
                    <span>{selectedTransaksi.metode_pembayaran}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status</label>
                    <span>
                      <span className={`badge ${
                        selectedTransaksi.status_pembayaran === 'Lunas' ? 'badge-success' :
                        selectedTransaksi.status_pembayaran === 'Belum Lunas' ? 'badge-danger' :
                        'badge-warning'
                      }`}>
                        {selectedTransaksi.status_pembayaran}
                      </span>
                    </span>
                  </div>
                  {selectedTransaksi.bukti_pembayaran && (
                    <div className="detail-item">
                      <label>Bukti Pembayaran</label>
                      <span>{selectedTransaksi.bukti_pembayaran}</span>
                    </div>
                  )}
                  {selectedTransaksi.keterangan && (
                    <div className="detail-item full-width">
                      <label>Keterangan</label>
                      <span>{selectedTransaksi.keterangan}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseDetailModal}>
                Tutup
              </button>
              <button className="btn btn-primary">
                <FiDownload /> Cetak Bukti
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PembayaranMahasiswa;
